import { CaretUpIcon, CarIcon, GearSixIcon, PackageIcon, PlusIcon } from '@phosphor-icons/react'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Outlet, useParams } from 'react-router-dom'

import { useGetPackageRoomAccessCode } from '@/api/packages/get-package-room-access-code'
import { useGetPackagesSettings } from '@/api/packages/get-packages-settings'
import { PackagesSettings } from '@/components/business/packages-settings'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { NavbarItem } from '@/components/layout/page-header/page-header.types'
import { Button } from '@/components/ui/button'
import { DropdownMenu } from '@/components/ui/dropdown-menu'
import { Icon } from '@/components/ui/icon'
import { popupFactory, toastFactory } from '@/factories'
import { AccessCodeModal } from '@/pages/packages/access-code-modal'
import { DriversAccessCodeModal } from '@/pages/packages/drivers-access-code-modal'
import { PackageFormModal } from '@/pages/packages/package-form-modal'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { getApiErrorMessage } from '@/utils'

import * as S from './packages.styled'

const getPackagesNavbarItem = (t: TFunction<['packages']>): NavbarItem => ({
  label: t('packages:page.navigation.packages'),
  to: {
    path: '/dashboard/building-dashboard/packages/list',
  },
})

const getPackageRoomLogNavbarItem = (t: TFunction<['packages']>): NavbarItem => ({
  label: t('packages:page.navigation.packageRoomLog'),
  to: { path: '/dashboard/building-dashboard/packages/log' },
})

export const PackagesPage = () => {
  const { t } = useTranslation(['packages'])
  const { sessionId } = useParams()
  const selectedBuilding = useSelector(selectActiveBuilding)

  const buildingId = selectedBuilding?.id
  const isPackagesD2DAvailable = selectedBuilding?.packagesD2DAvailable ?? false
  const isQuickScanAvailable = selectedBuilding?.packagesQuickScanAvailable ?? false
  const packageRoomAvailable = selectedBuilding?.packageRoomAvailable ?? false
  const { data: packagesSettings } = useGetPackagesSettings({ buildingId: buildingId ?? 0 })
  const carrierAccessCodeEnabled = packagesSettings?.carrierAccessCodeEnabled ?? false

  const { mutateAsync: getPackageRoomCode, isPending: isRequestingPackageRoomCode } =
    useGetPackageRoomAccessCode()

  const createNewPackage = () => {
    popupFactory.open(PackageFormModal, {
      mode: 'create',
      packageData: null,
    })
  }

  const requestPackageRoomAccessCode = async () => {
    if (!buildingId) return

    try {
      const response = await getPackageRoomCode({ buildingId })
      popupFactory.open(AccessCodeModal, {
        accessCode: response.code,
      })
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const requestDriversAccessCode = () => {
    if (!buildingId) return

    popupFactory.open(DriversAccessCodeModal, {
      buildingId,
    })
  }

  const openSettings = () => {
    if (!buildingId) return

    popupFactory.open(PackagesSettings, {
      buildingId,
      isPackagesD2DAvailable,
      isQuickScanAvailable,
    })
  }

  // Note: If sessionId is present, it means that we are in the package room log page.
  if (sessionId) {
    return (
      <Page>
        <Outlet />
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title={t('packages:page.title')}
        navbarItems={[
          getPackagesNavbarItem(t),
          ...(packageRoomAvailable ? [getPackageRoomLogNavbarItem(t)] : []),
        ]}
        actions={
          <>
            {(packageRoomAvailable || carrierAccessCodeEnabled) && (
              <DropdownMenu
                items={[
                  {
                    hidden: !packageRoomAvailable,
                    icon: PackageIcon,
                    label: t('packages:page.actions.requestPackageRoomAccess'),
                    onClick: requestPackageRoomAccessCode,
                  },
                  {
                    hidden: !carrierAccessCodeEnabled,
                    icon: CarIcon,
                    label: t('packages:page.actions.requestDriversAccess'),
                    onClick: requestDriversAccessCode,
                  },
                ]}
                trigger={({ isOpen }) => (
                  <Button use="secondary" size="small" loading={isRequestingPackageRoomCode}>
                    {t('packages:page.actions.requestAccessCode')}
                    <S.CaretIcon icon={CaretUpIcon} $isOpen={isOpen} />
                  </Button>
                )}
              />
            )}
            <S.VerticalSeparator />
            <Button use="secondary" size="small" onClick={openSettings}>
              <Icon icon={GearSixIcon} />
              {t('packages:page.actions.settings')}
            </Button>
            <Button use="primary" size="small" onClick={createNewPackage}>
              {t('packages:page.createPackage')}
              <Icon icon={PlusIcon} />
            </Button>
          </>
        }
      />
      <Outlet />
    </Page>
  )
}
